<script setup>
import { onMounted, ref, computed, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import ImageLogo from "@components/images/ImageLogo.vue";
import Network from "@components/modals/Network.vue";
import SearchBar from "@components/search_bars/SearchBar.vue";
import NavigationMenuCollapse from "@components/modals/NavigationMenuCollapse.vue";
import UserMenu from "@components/auth/UserMenu.vue";
import ShowsButton from "@components/buttons/ShowsButton.vue";
import { useBrandsStore } from "@composables/use_brands_store";
import { useInventoryStore } from "@composables/use_inventory_store";
import logosSprite from "/src/assets/img/logos_sprite.jpg";
import { useAuthStore } from "@composables/use_auth_store";
import { useShoppingCartStore } from "@composables/use_shopping_cart_store";
import Icon from "@components/icons/Icon.vue";
import { useTextBannersStore } from "@composables/use_textbanners_store";

import "vue3-carousel/dist/carousel.css";
import { Carousel, Slide } from "vue3-carousel";
import { siExistenDatosEncriptar } from "@composables/use_encript.js";

const { shoppingCartItemCount } = useShoppingCartStore();
const router = useRouter();
const route = useRoute();
const isShowsRoute = computed(() => {
  return route.name === 'Shows' || route.name === 'ShowGallery';
});
const { brands } = useBrandsStore();
const {
  criteria,
  setCategory,
  setSubcategory,
  setCriteria,
  getProductBySubcategory,
  searchProduct,
  clearSearch,
} = useInventoryStore();

const { token, cliente } = useAuthStore();
const { getTextBanners } = useTextBannersStore();

const textBanners = ref([]);
const soloPirotecnia = ref(false);

watch(
  () => route.fullPath,
  (newPath) => {
    soloPirotecnia.value = newPath.includes("pirotecnia") || 
      (typeof window !== "undefined" && window.location.href.includes("?pirotecnia"));
  },
  { immediate: true }
);

const NavItem = computed(() => [
  { name: "Home", link: soloPirotecnia.value ? "/?pirotecnia/" : "/" },
]);

onMounted(async () => {
  siExistenDatosEncriptar();
  textBanners.value = await getTextBanners();
});

function goTo(logo_src) {
  let pirotecnia = logo_src == "logoBasterboom" ? true : false;
  let btnClearSearch = document.getElementById("btnClearSearch");
  if (btnClearSearch) {
    btnClearSearch.click();
  }
  window.location.href = pirotecnia
    ? window.location.origin + "/?pirotecnia"
    : window.location.origin;
}

function onSearch(criteria) {
  setCategory(null);
  setSubcategory(null);
  setCriteria(criteria);

  switch (route.name) {
    case "Category":
      const cat_url_seg_arr = route.params.category_id_slug.split("_");
      setCategory(cat_url_seg_arr[0]);
      searchProduct();
      break;
    case "Subcategory":
      const cat_sub_seg_arr = route.params.category_id_slug.split("_");
      setCategory(cat_sub_seg_arr[0]);
      const sub_url_seg_arr = route.params.subcategory_id_slug.split("_");
      setSubcategory(sub_url_seg_arr[0]);
      if (criteria) {
        searchProduct();
      } else {
        clearSearch();
        getProductBySubcategory(cat_sub_seg_arr[0], sub_url_seg_arr[0]);
      }
      break;
    default:
      searchProduct();
      break;
  }
}

function hideAllMenu() {
  let linksMenu = document.querySelectorAll(".footer-network-link");
  if (linksMenu) {
    linksMenu.forEach((item) => {
      item.classList.add("d-none");
    });
  }
}
</script>

<template>
  <header
    class="fixed-nav"
    :class="isShowsRoute ? 'bg-[#08090d]' : 'bg-white'"
    @click="hideAllMenu()"
  >
    <!-- Banner -->
    <carousel
      v-if="textBanners.length"
      :items-to-show="1"
      :autoplay="8000"
      :wrapAround="true"
      class="text-white mt-0 py-1 text-[11px] sm:text-[13px]"
      style="background-color: #333"
    >
      <slide v-for="(textBanner, index) in textBanners" :key="index">
        <em><a class="text-white" :href="textBanner.link || '#'" target="_blank">{{ textBanner.texto }}</a></em>
      </slide>
    </carousel>

    <!-- Host Div: Universal Header Container -->
    <div
      class="header-host relative bg-[#e41b1b]"
      :class="isShowsRoute ? '' : 'shadow-xl'"
    >
      
      <!-- WHITE PANEL OVERLAY (Both Rows) -->
      <div class="absolute inset-0 z-0">
        <!-- Gray Stripe (10px wider than white panel) -->
        <div
          class="absolute h-full bg-gray-300"
          :class="isShowsRoute ? 'slash-stripe-no-shadow' : 'slash-stripe'"
        ></div>
        <!-- White Panel -->
        <div
          class="white-panel-shape h-full bg-white"
          :class="isShowsRoute ? '' : 'shadow-[10px_0_15px_-5px_rgba(0,0,0,0.4)]'"
        ></div>
      </div>

      <!-- CONTENT LAYER -->
      <nav class="relative z-10 flex flex-col">
        <!-- ROW 1: Logos -->
        <div class="flex h-[64px] sm:h-[72px] items-center">
          <!-- Logos Area (Using Sprite) -->
          <div class="flex items-center pl-2 pr-1 md:px-4 gap-1.5 sm:gap-6 w-full max-w-fit">
            <ImageLogo
              :sprite="true"
              sprite-pos="0%"
              :classes="'h-12 w-9 sm:h-16 sm:w-12'"
              :source="logosSprite"
              @ontap="goTo('logoBaspack')"
            />
            <ImageLogo
              :sprite="true"
              sprite-pos="47%"
              :classes="'h-12 w-12 sm:h-16 sm:w-16'"
              :source="logosSprite"
              @ontap="goTo('logoBasterboom')"
            />
            <ImageLogo
              :sprite="true"
              sprite-pos="94%"
              :classes="'h-12 w-6 sm:h-16 sm:w-8'"
              :source="logosSprite"
              @ontap="goTo('logoCorbas')"
            />
            <Network
              :type="'whatsapp2'"
              id="link_whatsapp2"
              :brands="brands"
              icon-class="h-9 w-9 sm:h-12 sm:w-12"
            />
          </div>
          <!-- Top Right Actions: Shows Button + User Menu -->
          <div class="flex-1 flex justify-end items-center gap-1.5 sm:gap-3 pl-1 pr-2 sm:px-8">
            <ShowsButton v-if="soloPirotecnia" />
            <div v-if="token">
              <UserMenu :cliente="cliente" />
            </div>
          </div>
        </div>

        <!-- ROW 2: Actions -->
        <div class="flex pt-1 pb-2 sm:pb-3 items-center px-2 sm:px-4 md:px-8 gap-3 sm:gap-6">
          <!-- Hamburger Menu (on white side) -->
          <div class="flex-none z-20">
            <NavigationMenuCollapse :items="NavItem" :showCart="false" />
          </div>

          <!-- Search & Cart (on red side) -->
          <div class="flex-1 flex items-center gap-3 sm:gap-6 min-w-0">
            <div class="flex-1 min-w-0">
              <SearchBar :criteria="criteria" @onsearchbarchange="onSearch" />
            </div>
            
            <div class="flex-none pr-2 sm:pr-3 md:pr-2">
              <a
                class="relative flex items-center justify-center p-1"
                :href="`${shoppingCartItemCount > 0 ? (soloPirotecnia ? '/shopping_cart?pirotecnia' : '/shopping_cart') : '#'}`"
              >
                <Icon :name="'shopping_cart'" :width="34" class="text-white" />
                <span
                  v-if="shoppingCartItemCount > 0"
                  class="absolute -top-1 -right-1 min-w-[20px] h-[20px] flex items-center justify-center bg-white text-[#e41b1b] text-[11px] font-black rounded-full px-1 shadow-lg"
                >{{ shoppingCartItemCount }}</span>
              </a>
            </div>
          </div>
        </div>
      </nav>
    </div>
  </header>
</template>

<style scoped>
.fixed-nav {
  position: fixed;
  top: 0;
  width: 100%;
  z-index: 150;
}

/* 
  The White Panel Shape:
  Reduced width to bring the diagonal closer to the logos/WhatsApp.
*/
.slash-stripe {
  width: 210px; /* Mobile width + 10px */
  clip-path: polygon(0 0, 100% 0, calc(100% - 30px) 100%, 0 100%);
  filter: drop-shadow(10px 0 8px rgba(0,0,0,0.4));
}

.slash-stripe-no-shadow {
  width: 210px; /* Mobile width + 10px */
  clip-path: polygon(0 0, 100% 0, calc(100% - 30px) 100%, 0 100%);
}

.white-panel-shape {
  width: 200px; /* Mobile width */
  clip-path: polygon(0 0, 100% 0, calc(100% - 30px) 100%, 0 100%);
}

@media (min-width: 640px) {
  .slash-stripe {
    width: 330px; /* Desktop width + 10px */
    clip-path: polygon(0 0, 100% 0, calc(100% - 60px) 100%, 0 100%);
  }
  .slash-stripe-no-shadow {
    width: 330px; /* Desktop width + 10px */
    clip-path: polygon(0 0, 100% 0, calc(100% - 60px) 100%, 0 100%);
  }
  .white-panel-shape {
    width: 320px; /* Desktop width */
    clip-path: polygon(0 0, 100% 0, calc(100% - 60px) 100%, 0 100%);
  }
}

/* Red hamburger icon on white background */
:deep(.navbar-toggler) {
  border: none !important;
  padding: 0 !important;
  /* Filter to convert black/dark icon to #e41b1b red */
  filter: invert(19%) sepia(94%) saturate(5412%) hue-rotate(355deg) brightness(90%) contrast(98%);
}

/* Search bar refinements */
:deep(.search-bar-container input) {
  padding-left: 2.5rem !important;
}

:deep(.search-bar-container) {
  border: 1.5px solid #d1d5db !important; /* Light gray border */
  border-radius: 9999px !important;
  max-width: 100%;
  background-color: #fff !important;
}
</style>
